import { format } from "date-fns";
import { motion } from "framer-motion";
import { useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import { Link } from "react-router-dom";
import Container from "../components/Container";
import Loading from "../components/Loading";
import { MarkdownStyle } from "../components/markdown/Markdown";
import pb from "../lib/pocketbase";
import { IBlogRecord } from "../lib/types";
import { createSlug } from "../lib/utils";

export default function Blog() {
    const [isLoading, setIsLoading] = useState(true);
    const [blogPosts, setBlogPosts] = useState<IBlogRecord[]>();

    async function load() {
        const bp: IBlogRecord[] = await pb.collection("blog").getFullList({
            sort: "-published"
        });
        setBlogPosts(bp);
        setIsLoading(false);
    }

    useEffect(() => {
        load();
    }, []);

    const groupedPosts: { [year: string]: IBlogRecord[] } = {};
    blogPosts?.forEach((post) => {
        const year = new Date(post.published || post.created).getFullYear().toString();
        if (!groupedPosts[year]) {
            groupedPosts[year] = [];
        }
        groupedPosts[year].push(post);
    });

    return (
        <>
            <Helmet>
                <title>ReinVB — Blog</title>
                <meta name="description" content="The latest and greatest blogposts of Rein." />
            </Helmet>
            <Container className="mt-4 lg:mt-8 xl:mt-16">
                <MarkdownStyle className="max-w-full pl-4">
                    <h1>Blog.</h1>
                    <motion.hr animate={{ width: "100%" }} transition={{ duration: 2 }} className={`-mt-6 w-0 max-w-[5rem] rounded border-t-4 border-indigo-400 dark:border-indigo-400`} />
                </MarkdownStyle>
                {isLoading ? (
                    <div className="flex w-full items-center justify-center">
                        <Loading />
                    </div>
                ) : (
                    <>
                        {!blogPosts?.length && <p className="mt-8 pl-4 italic text-slate-500">There are currently no blogposts.</p>}
                        {Object.keys(groupedPosts)
                            .reverse()
                            .map((year) => (
                                <div key={year} className="mt-8">
                                    <h2 className="pl-4 text-xl font-medium">{year}</h2>
                                    <div className="mt-4 flex max-w-2xl flex-col gap-4">
                                        {groupedPosts[year].map((post) => (
                                            <Link to={`/blog/${createSlug(post.title)}/${post.id}`} className="group flex items-center gap-4 rounded-xl px-4 py-4 transition-colors hover:bg-indigo-400/10">
                                                <span className="text-slate-400">{format(new Date(post.published || post.created), "dd MMM")}</span>
                                                <h2 className="text-lg font-medium transition-colors group-hover:text-indigo-600 dark:group-hover:text-indigo-400">{post.title}</h2>
                                            </Link>
                                        ))}
                                    </div>
                                </div>
                            ))}
                    </>
                )}
            </Container>
        </>
    );
}
